<script setup>
import { ref, computed } from 'vue';

import FormInput from 'v3/components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// The second factor asked for at the door of a sensitive PowerApp. Kept apart
// from components/auth/MfaVerification.vue on purpose: that one posts to
// /auth/sign_in and redirects to /app/, so it only serves the login step.
defineProps({
  errorMessage: { type: String, default: '' },
  isVerifying: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const OTP_LENGTH = 6;
const BACKUP_LENGTH = 8;

const useBackupCode = ref(false);
const code = ref('');

const maxLength = computed(() =>
  useBackupCode.value ? BACKUP_LENGTH : OTP_LENGTH
);
const canSubmit = computed(() => code.value.length === maxLength.value);

const toggleMethod = () => {
  useBackupCode.value = !useBackupCode.value;
  code.value = '';
};

const submit = () => {
  if (!canSubmit.value) return;
  emit(
    'submit',
    useBackupCode.value ? { backup_code: code.value } : { otp_code: code.value }
  );
};
</script>

<template>
  <form
    class="flex flex-col self-center w-80 gap-4 p-6 m-auto border rounded-xl bg-n-solid-1 border-n-weak"
    @submit.prevent="submit"
  >
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('POWER_APPS.MFA.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{
          useBackupCode
            ? $t('POWER_APPS.MFA.BACKUP_DESCRIPTION')
            : $t('POWER_APPS.MFA.DESCRIPTION')
        }}
      </p>
    </div>
    <FormInput
      v-model="code"
      name="powerapp_mfa_code"
      :label="
        useBackupCode
          ? $t('POWER_APPS.MFA.BACKUP_LABEL')
          : $t('POWER_APPS.MFA.LABEL')
      "
      :maxlength="maxLength"
      autocomplete="one-time-code"
      autofocus
      :has-error="!!errorMessage"
      :error-message="errorMessage"
      spacing="compact"
    />
    <NextButton
      type="submit"
      :label="$t('POWER_APPS.MFA.SUBMIT')"
      :is-loading="isVerifying"
      :disabled="!canSubmit || isVerifying"
      blue
    />
    <NextButton
      type="button"
      variant="link"
      :label="
        useBackupCode
          ? $t('POWER_APPS.MFA.USE_OTP')
          : $t('POWER_APPS.MFA.USE_BACKUP')
      "
      @click="toggleMethod"
    />
  </form>
</template>
